import React, { useState, useEffect, useCallback } from 'react';
import {
    PlusIcon,
    DocumentDuplicateIcon,
    PencilIcon,
    TrashIcon,
    UserPlusIcon,
    ChartBarIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import LayoutWrapper from '../components/LayoutWrapper';
import Helper from '../helpers';
import config from '../config';

const baseUrl = config.apiBaseUrl;
const DEFAULT_POINTS_VALUE = 10;

const PlanManagement = () => {
    const [currentVenueKey, setCurrentVenueKey] = useState("");
    const [activeTab, setActiveTab] = useState('templates'); // 'templates' or 'assigned'
    const [templates, setTemplates] = useState([]);
    const [assignedPlans, setAssignedPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showExerciseModal, setShowExerciseModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [exercises, setExercises] = useState([]);
    const [venueUsers, setVenueUsers] = useState([]);

    // Form states
    const [templateForm, setTemplateForm] = useState({
        name: '',
        description: '',
        isActive: true,
        completionBonusPoints: 0
    });
    const [exerciseForm, setExerciseForm] = useState({
        name: '',
        description: '',
        videoUrl: '',
        dayNumber: 1, // Default value for backend compatibility (not shown to user)
        orderIndex: 1,
        sets: 3,
        reps: 10,
        durationSeconds: null,
        restSeconds: 60,
        notes: '',
        pointsValue: 10
    });
    const [assignForm, setAssignForm] = useState({
        userId: '',
        customizationNotes: '',
        startDate: new Date().toISOString().split('T')[0],
        scheduleType: 'sequential', // 'sequential' or 'specific_days'
        dayMapping: [], // Array of selected days for the whole plan
        exercisePoints: {} // Points per exercise for this assignment
    });


    const fetchTemplates = useCallback(async () => {
        if (!currentVenueKey) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/templates`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setTemplates(data);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    }, [currentVenueKey]);

    const fetchAssignedPlans = useCallback(async () => {
        if (!currentVenueKey) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/assigned`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setAssignedPlans(data);
            }
        } catch (error) {
            console.error('Error fetching assigned plans:', error);
        }
    }, [currentVenueKey]);

    const fetchAnalytics = useCallback(async () => {
        if (!currentVenueKey) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/analytics`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setAnalytics(data);
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
        }
    }, [currentVenueKey]);

    const fetchVenueUsers = useCallback(async () => {
        if (!currentVenueKey) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/users/venueusers/${currentVenueKey}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setVenueUsers(data);
            } else {
                console.error('Failed to fetch venue users:', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error fetching venue users:', error);
        }
    }, [currentVenueKey]);

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            const venueKey = await Helper.getUserVenueId();
            setCurrentVenueKey(venueKey);
        };
        initialize();
    }, []);

    useEffect(() => {
        if (currentVenueKey) {
            if (activeTab === 'templates') {
                fetchTemplates();
            } else if (activeTab === 'assigned') {
                fetchAssignedPlans();
            } else if (activeTab === 'analytics') {
                fetchAnalytics();
            }
            setLoading(false);
        }
    }, [currentVenueKey, activeTab, fetchTemplates, fetchAssignedPlans, fetchAnalytics]);

    const handleCreateTemplate = async (e) => {
        e.preventDefault();

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/templates`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(templateForm)
            });

            if (response.ok) {
                setShowCreateModal(false);
                setTemplateForm({ name: '', description: '', isActive: true, completionBonusPoints: 0 });
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error creating template:', error);
        }
    };

    const handleManageExercises = async (template) => {
        setSelectedTemplate(template);
        
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/templates/${template.id}/exercises`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setExercises(data);
                setShowExerciseModal(true);
            }
        } catch (error) {
            console.error('Error fetching exercises:', error);
        }
    };

    const handleAddExercise = async (e) => {
        e.preventDefault();

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/templates/${selectedTemplate.id}/exercises`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(exerciseForm)
            });

            if (response.ok) {
                const newExercise = await response.json();
                setExercises([...exercises, newExercise]);
                setExerciseForm({
                    name: '',
                    description: '',
                    videoUrl: '',
                    dayNumber: 1, // Default for backend
                    orderIndex: exercises.length + 1,
                    sets: 3,
                    reps: 10,
                    durationSeconds: null,
                    restSeconds: 60,
                    notes: '',
                    pointsValue: 10
                });
            }
        } catch (error) {
            console.error('Error adding exercise:', error);
        }
    };

    const handleDeleteExercise = async (exerciseId) => {
        if (!window.confirm('Are you sure you want to delete this exercise?')) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/exercises/${exerciseId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                setExercises(exercises.filter(e => e.id !== exerciseId));
            }
        } catch (error) {
            console.error('Error deleting exercise:', error);
        }
    };

    const handleAssignPlan = async (e) => {
        e.preventDefault();

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/assign`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    templateId: selectedTemplate.id,
                    ...assignForm,
                    dayMapping: assignForm.scheduleType === 'specific_days' ? JSON.stringify(assignForm.dayMapping) : null,
                    exercisePoints: Object.keys(assignForm.exercisePoints).length > 0 ? JSON.stringify(assignForm.exercisePoints) : null
                })
            });

            if (response.ok) {
                setShowAssignModal(false);
                setAssignForm({ 
                    userId: '', 
                    customizationNotes: '', 
                    startDate: new Date().toISOString().split('T')[0],
                    scheduleType: 'sequential',
                    dayMapping: [],
                    exercisePoints: {}
                });
                fetchAssignedPlans();
            }
        } catch (error) {
            console.error('Error assigning plan:', error);
        }
    };

    const handleDuplicateTemplate = async (templateId) => {
        const newName = prompt('Enter name for duplicated template:');
        if (!newName) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/templates/${templateId}/duplicate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    venueKey: currentVenueKey,
                    templateId,
                    newName
                })
            });

            if (response.ok) {
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error duplicating template:', error);
        }
    };

    const handleDeleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/venues/${currentVenueKey}/plans/templates/${templateId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                fetchTemplates();
            }
        } catch (error) {
            console.error('Error deleting template:', error);
        }
    };

    if (loading) {
        return (
            <LayoutWrapper title="Plan Management">
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper
            title="Workout Plan Management"
            subtitle="Create and manage training plans for your clients"
        >
            {/* Tabs */}
            <div className="mb-8">
                <div className="flex flex-wrap gap-2 sm:gap-4 border-b border-gray-700">
                    <button
                        onClick={() => setActiveTab('templates')}
                        className={`px-4 py-2 text-sm sm:text-base font-medium transition-colors duration-200 border-b-2 ${
                            activeTab === 'templates'
                                ? 'border-emerald-500 text-emerald-400'
                                : 'border-transparent text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        Templates
                    </button>
                    <button
                        onClick={() => setActiveTab('assigned')}
                        className={`px-4 py-2 text-sm sm:text-base font-medium transition-colors duration-200 border-b-2 ${
                            activeTab === 'assigned'
                                ? 'border-emerald-500 text-emerald-400'
                                : 'border-transparent text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        Assigned Plans
                    </button>
                    <button
                        onClick={() => setActiveTab('analytics')}
                        className={`px-4 py-2 text-sm sm:text-base font-medium transition-colors duration-200 border-b-2 ${
                            activeTab === 'analytics'
                                ? 'border-emerald-500 text-emerald-400'
                                : 'border-transparent text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        <ChartBarIcon className="h-5 w-5 inline mr-1" />
                        Analytics
                    </button>
                </div>
            </div>

            {/* Templates Tab */}
            {activeTab === 'templates' && (
                <div>
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-semibold text-white">Plan Templates</h2>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                        >
                            <PlusIcon className="h-5 w-5" />
                            Create Template
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {templates.map(template => (
                            <div key={template.id} className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-lg font-semibold text-white mb-1">{template.name}</h3>
                                        <p className="text-sm text-gray-400">{template.description}</p>
                                        <p className="text-xs text-emerald-400 mt-2">{template.exerciseCount} exercises</p>
                                    </div>
                                    {template.isActive ? (
                                        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded">Active</span>
                                    ) : (
                                        <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded">Inactive</span>
                                    )}
                                </div>

                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={() => handleManageExercises(template)}
                                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm transition-colors"
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                        Exercises
                                    </button>
                                    <button
                                        onClick={() => handleDuplicateTemplate(template.id)}
                                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm transition-colors"
                                    >
                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                        Duplicate
                                    </button>
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <button
                                        onClick={() => {
                                            setSelectedTemplate(template);
                                            fetchVenueUsers();
                                            setShowAssignModal(true);
                                        }}
                                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm transition-colors"
                                    >
                                        <UserPlusIcon className="h-4 w-4" />
                                        Assign
                                    </button>
                                    <button
                                        onClick={() => handleDeleteTemplate(template.id)}
                                        className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {templates.length === 0 && (
                        <div className="text-center py-12 bg-gray-900 border border-gray-700 rounded-lg">
                            <p className="text-gray-400">No templates created yet. Click "Create Template" to get started.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Assigned Plans Tab */}
            {activeTab === 'assigned' && (
                <div>
                    <h2 className="text-xl font-semibold text-white mb-6">Assigned Plans</h2>

                    <div className="space-y-4">
                        {assignedPlans.map(plan => (
                            <div key={plan.id} className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-white">{plan.templateName}</h3>
                                        <p className="text-sm text-gray-400 mt-1">Assigned to: {plan.userName}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            Assigned: {new Date(plan.assignedDate).toLocaleDateString()}
                                        </p>
                                        {plan.customizationNotes && (
                                            <p className="text-sm text-blue-300 mt-2 italic">"{plan.customizationNotes}"</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <span className={`px-3 py-1 rounded text-sm ${
                                            plan.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
                                            plan.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                                            'bg-gray-500/20 text-gray-400'
                                        }`}>
                                            {plan.status}
                                        </span>
                                        <div className="text-right">
                                            <p className="text-sm text-emerald-400">🏆 {plan.totalPoints} points</p>
                                            <p className="text-sm text-orange-400">🔥 {plan.currentStreak} day streak</p>
                                            <p className="text-xs text-gray-500">Best: {plan.longestStreak} days</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 bg-gray-800 rounded-lg p-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400">Progress:</span>
                                        <span className="text-white">{plan.completedExercises} / {plan.totalExercises} exercises</span>
                                    </div>
                                    <div className="mt-2 bg-gray-700 rounded-full h-2">
                                        <div 
                                            className="bg-emerald-500 h-2 rounded-full transition-all"
                                            style={{ width: `${plan.totalExercises > 0 ? (plan.completedExercises / plan.totalExercises) * 100 : 0}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {assignedPlans.length === 0 && (
                        <div className="text-center py-12 bg-gray-900 border border-gray-700 rounded-lg">
                            <p className="text-gray-400">No plans assigned yet.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
                <div>
                    <h2 className="text-xl font-semibold text-white mb-6">Analytics Dashboard</h2>
                    
                    {analytics ? (
                        <div className="space-y-6">
                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                                    <p className="text-gray-400 text-sm">Total Assignments</p>
                                    <p className="text-3xl font-bold text-white mt-2">{analytics.totalAssignments}</p>
                                </div>
                                <div className="bg-gray-900 border border-emerald-700 rounded-lg p-6">
                                    <p className="text-gray-400 text-sm">Active Plans</p>
                                    <p className="text-3xl font-bold text-emerald-400 mt-2">{analytics.activeAssignments}</p>
                                </div>
                                <div className="bg-gray-900 border border-blue-700 rounded-lg p-6">
                                    <p className="text-gray-400 text-sm">Completed</p>
                                    <p className="text-3xl font-bold text-blue-400 mt-2">{analytics.completedAssignments}</p>
                                </div>
                                <div className="bg-gray-900 border border-orange-700 rounded-lg p-6">
                                    <p className="text-gray-400 text-sm">Avg Completion</p>
                                    <p className="text-3xl font-bold text-orange-400 mt-2">{analytics.averageCompletionRate.toFixed(1)}%</p>
                                </div>
                            </div>

                            {/* Leaderboard */}
                            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-white mb-4">🏆 Top Performers</h3>
                                <div className="space-y-3">
                                    {analytics.topPerformers.map((performer, index) => (
                                        <div key={index} className="flex items-center justify-between bg-gray-800 rounded-lg p-4">
                                            <div className="flex items-center gap-4">
                                                <span className={`text-2xl font-bold ${
                                                    index === 0 ? 'text-yellow-400' :
                                                    index === 1 ? 'text-gray-300' :
                                                    index === 2 ? 'text-orange-400' :
                                                    'text-gray-500'
                                                }`}>
                                                    #{index + 1}
                                                </span>
                                                <div>
                                                    <p className="text-white font-medium">{performer.displayName || performer.userName || 'Unknown'}</p>
                                                    <p className="text-sm text-gray-400">🔥 {performer.currentStreak} day streak</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-emerald-400 font-bold">{performer.totalPoints} pts</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {analytics.topPerformers.length === 0 && (
                                    <p className="text-gray-400 text-center py-8">No data yet. Assign some plans to get started!</p>
                                )}
                            </div>

                            {/* Total Exercises Completed */}
                            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-white mb-2">Total Exercises Completed</h3>
                                <p className="text-4xl font-bold text-emerald-400">{analytics.totalExercisesCompleted}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                        </div>
                    )}
                </div>
            )}

            {/* Create Template Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-xl font-semibold text-white mb-4">Create New Template</h3>
                        <form onSubmit={handleCreateTemplate}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Plan Name *
                                    </label>
                                    <input
                                        type="text"
                                        value={templateForm.name}
                                        onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                        placeholder="e.g., Push/Pull/Legs, Beginner Program"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Short Description
                                    </label>
                                    <textarea
                                        value={templateForm.description}
                                        onChange={(e) => setTemplateForm({...templateForm, description: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                        rows="2"
                                        placeholder="Brief overview of this plan..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Completion Bonus Points
                                    </label>
                                    <input
                                        type="number"
                                        value={templateForm.completionBonusPoints}
                                        onChange={(e) => setTemplateForm({...templateForm, completionBonusPoints: parseInt(e.target.value) || 0})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                        placeholder="Bonus points for completing all exercises (e.g., 50)"
                                        min="0"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Extra points awarded when all exercises in the plan are completed in one day</p>
                                </div>
                                <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
                                    <p className="text-xs text-gray-400">
                                        💡 <strong>Tip:</strong> After creating the template, add exercises to define the actual workouts and point values.
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                                >
                                    Create Template
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Exercise Management Modal */}
            {showExerciseModal && selectedTemplate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-gray-900 rounded-lg p-6 max-w-4xl w-full my-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-semibold text-white">Manage Exercises - {selectedTemplate.name}</h3>
                            <button onClick={() => setShowExerciseModal(false)} className="text-gray-400 hover:text-white">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Add Exercise Form */}
                        <form onSubmit={handleAddExercise} className="mb-6 bg-gray-800 rounded-lg p-4">
                            <h4 className="text-lg font-medium text-white mb-4">Add New Exercise</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Exercise Name *</label>
                                    <input
                                        type="text"
                                        value={exerciseForm.name}
                                        onChange={(e) => setExerciseForm({...exerciseForm, name: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Video URL (optional)</label>
                                    <input
                                        type="url"
                                        value={exerciseForm.videoUrl}
                                        onChange={(e) => setExerciseForm({...exerciseForm, videoUrl: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                        placeholder="https://youtube.com/watch?v=..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Sets</label>
                                    <input
                                        type="number"
                                        value={exerciseForm.sets || ''}
                                        onChange={(e) => setExerciseForm({...exerciseForm, sets: e.target.value ? parseInt(e.target.value) : null})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Reps</label>
                                    <input
                                        type="number"
                                        value={exerciseForm.reps || ''}
                                        onChange={(e) => setExerciseForm({...exerciseForm, reps: e.target.value ? parseInt(e.target.value) : null})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Duration (seconds)</label>
                                    <input
                                        type="number"
                                        value={exerciseForm.durationSeconds || ''}
                                        onChange={(e) => setExerciseForm({...exerciseForm, durationSeconds: e.target.value ? parseInt(e.target.value) : null})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                        placeholder="e.g., 60 for 1 min"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Rest (seconds)</label>
                                    <input
                                        type="number"
                                        value={exerciseForm.restSeconds || ''}
                                        onChange={(e) => setExerciseForm({...exerciseForm, restSeconds: e.target.value ? parseInt(e.target.value) : null})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-300 mb-1">Points Value *</label>
                                    <input
                                        type="number"
                                        value={exerciseForm.pointsValue}
                                        onChange={(e) => setExerciseForm({...exerciseForm, pointsValue: parseInt(e.target.value) || 10})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                        placeholder="Points (default: 10)"
                                        min="0"
                                        required
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm text-gray-300 mb-1">Description</label>
                                    <textarea
                                        value={exerciseForm.description}
                                        onChange={(e) => setExerciseForm({...exerciseForm, description: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                                        rows="2"
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                            >
                                <PlusIcon className="h-4 w-4 inline mr-1" />
                                Add Exercise
                            </button>
                        </form>

                        {/* Exercise List */}
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            <h4 className="text-lg font-medium text-white sticky top-0 bg-gray-900 py-2">Current Exercises ({exercises.length})</h4>
                            {exercises.length === 0 && (
                                <p className="text-gray-400 text-center py-8">No exercises yet. Add some above!</p>
                            )}
                            {exercises.map((exercise) => {
                                return (
                                    <div key={exercise.id} className="bg-gray-800 rounded-lg p-4">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h5 className="text-white font-medium">{exercise.name}</h5>
                                                {exercise.description && (
                                                    <p className="text-sm text-gray-400 mt-1">{exercise.description}</p>
                                                )}
                                                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                                                    {exercise.sets && <span>Sets: {exercise.sets}</span>}
                                                    {exercise.reps && <span>Reps: {exercise.reps}</span>}
                                                    {exercise.durationSeconds && <span>Duration: {exercise.durationSeconds >= 60 ? `${Math.floor(exercise.durationSeconds / 60)}min ${exercise.durationSeconds % 60 > 0 ? `${exercise.durationSeconds % 60}s` : ''}` : `${exercise.durationSeconds}s`}</span>}
                                                    {exercise.restSeconds && <span>Rest: {exercise.restSeconds}s</span>}
                                                    <span className="text-yellow-400 font-medium">🏆 {exercise.pointsValue || 10} pts</span>
                                                    {exercise.videoUrl && <a href={exercise.videoUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">📹 Video</a>}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDeleteExercise(exercise.id)}
                                                className="ml-4 p-2 text-red-400 hover:text-red-300 transition-colors"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Plan Modal */}
            {showAssignModal && selectedTemplate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-white">Assign Plan to User</h3>
                            <button onClick={() => setShowAssignModal(false)} className="text-gray-400 hover:text-white">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <p className="text-gray-400 mb-4">Plan: <span className="text-white font-medium">{selectedTemplate.name}</span></p>
                        
                        <form onSubmit={handleAssignPlan}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Select User *</label>
                                    <select
                                        value={assignForm.userId}
                                        onChange={(e) => setAssignForm({...assignForm, userId: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                        required
                                    >
                                        <option value="">Choose a user...</option>
                                        {venueUsers.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.name} ({user.email})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
                                    <input
                                        type="date"
                                        value={assignForm.startDate}
                                        onChange={(e) => setAssignForm({...assignForm, startDate: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Schedule Type *</label>
                                    <select
                                        value={assignForm.scheduleType}
                                        onChange={(e) => setAssignForm({...assignForm, scheduleType: e.target.value, dayMapping: []})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                    >
                                        <option value="sequential">Ad Hoc (Complete exercises anytime)</option>
                                        <option value="specific_days">Specific Days of Week</option>
                                    </select>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {assignForm.scheduleType === 'sequential' 
                                            ? 'User completes exercises whenever they workout, no specific day restrictions'
                                            : 'Select which days of the week this plan applies to'}
                                    </p>
                                </div>
                                {assignForm.scheduleType === 'specific_days' && (
                                    <div className="bg-gray-800 rounded-lg p-4">
                                        <label className="block text-sm font-medium text-gray-300 mb-3">Plan Schedule Days</label>
                                        <p className="text-xs text-gray-400 mb-3">Select the days of the week when this plan should be performed.</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                                <label key={day} className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={assignForm.dayMapping.includes(day)}
                                                        onChange={(e) => {
                                                            const newDays = e.target.checked
                                                                ? [...assignForm.dayMapping, day]
                                                                : assignForm.dayMapping.filter(d => d !== day);
                                                            setAssignForm({...assignForm, dayMapping: newDays});
                                                        }}
                                                        className="h-4 w-4 text-emerald-600 bg-gray-700 border-gray-600 rounded focus:ring-emerald-500"
                                                    />
                                                    <span className="text-white text-sm">{day}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="bg-gray-800 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-3">Points per Exercise</label>
                                    <p className="text-xs text-gray-400 mb-3">Set how many points the user earns for completing each exercise. Default is 10 points.</p>
                                    {exercises.map(exercise => (
                                        <div key={exercise.id} className="flex items-center gap-3 mb-2">
                                            <span className="text-white text-sm flex-1 truncate">{exercise.name}:</span>
                                            <input
                                                type="number"
                                                min="1"
                                                value={assignForm.exercisePoints[exercise.id] || DEFAULT_POINTS_VALUE}
                                                onChange={(e) => setAssignForm({
                                                    ...assignForm,
                                                    exercisePoints: {...assignForm.exercisePoints, [exercise.id]: parseInt(e.target.value) || DEFAULT_POINTS_VALUE}
                                                })}
                                                className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
                                                placeholder={DEFAULT_POINTS_VALUE.toString()}
                                            />
                                            <span className="text-gray-400 text-sm">pts</span>
                                        </div>
                                    ))}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Personalized Notes</label>
                                    <textarea
                                        value={assignForm.customizationNotes}
                                        onChange={(e) => setAssignForm({...assignForm, customizationNotes: e.target.value})}
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                                        rows="3"
                                        placeholder="Add any specific instructions or modifications for this user..."
                                    />
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowAssignModal(false)}
                                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                                >
                                    Assign Plan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </LayoutWrapper>
    );
};

export default PlanManagement;
